import React from 'react';
import { Link } from 'react-router-dom';

const SecurityPage = () => {
  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Security & Data Protection</h1>
        <p className="mt-2 text-sm text-gray-500">
          Last updated: April 2026
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 sm:p-8 space-y-8 text-sm text-gray-700 leading-relaxed">
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Infrastructure</h2>
          <p>
            Car Wash Copilot is hosted entirely on <strong>Google Cloud Platform (GCP)</strong>, leveraging
            Google's world-class data center security, redundancy, and compliance certifications. Our
            application services run on <strong>Cloud Run</strong>, a fully managed serverless platform, and
            our database is hosted on <strong>Cloud SQL for PostgreSQL</strong> with automated backups,
            high availability, and point-in-time recovery.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Privacy</h2>
          <p>
            Your data belongs to you. Car Wash Copilot treats customer information as a trust, not
            a product. We make the following explicit commitments:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>
              <strong>We do not sell your information.</strong> We will never sell, rent, or lease
              your personal information, phone number, business data, financial records, or any
              other account data to anyone for any purpose.
            </li>
            <li>
              <strong>We do not share data across customers.</strong> Each business account is
              fully isolated. No other Car Wash Copilot customer can see your revenue, cash-outs,
              maintenance records, team members, phone numbers, or any other account data &mdash;
              ever.
            </li>
            <li>
              <strong>We do not use your data for marketing.</strong> Phone numbers and email
              addresses collected in the app are used solely for operational purposes (login
              verification, team invitations, work order notifications, billing). We do not send
              marketing messages, and we do not share contact details with marketers or data
              brokers.
            </li>
            <li>
              <strong>We do not monetize data through advertising.</strong> There is no advertising
              on Car Wash Copilot and no ad-tracking infrastructure. We have no business reason to
              profile your usage or build a saleable data product.
            </li>
            <li>
              <strong>We do not use your data to train AI models.</strong> Our AI chat assistant
              is provided by Anthropic under their API terms, which prohibit training on customer
              inputs or outputs. Your financial and operational data is never used to train any AI.
            </li>
            <li>
              <strong>We use a small number of trusted sub-processors.</strong> Your data is
              handled by: Google Cloud (hosting &amp; database), Firebase Authentication (login),
              Twilio (SMS delivery, phone number + message content only), Stripe (payment
              processing, billing contact only), and Anthropic (chat assistant, account context
              only). Each operates under strict enterprise data protection agreements.
            </li>
            <li>
              <strong>You can export or delete your data.</strong> Account owners can export their
              account data, and can request full deletion of their account and all associated
              records by contacting{' '}
              <a href="mailto:help@carwashcopilot.com" className="text-blue-600 hover:text-blue-800">
                help@carwashcopilot.com
              </a>
              . Deletion is performed within 30 days of request, subject to legal retention
              requirements (e.g., billing records retained for tax compliance).
            </li>
            <li>
              <strong>We only disclose data when legally required.</strong> We do not voluntarily
              share customer data with law enforcement or third parties. We will only disclose data
              in response to a valid legal process (e.g., subpoena, court order) and, where
              permitted, will notify the affected customer before doing so.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Encryption</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>In transit:</strong> All connections between your browser and our servers are encrypted
              using TLS 1.2 or higher (HTTPS). API traffic between internal services is also encrypted.
            </li>
            <li>
              <strong>At rest:</strong> All data stored in Cloud SQL is encrypted at rest using AES-256
              encryption, managed by Google's Key Management Service. Database backups are also encrypted.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Authentication</h2>
          <p>
            User authentication is handled by <strong>Firebase Authentication</strong>, Google's enterprise-grade
            identity platform. We use phone number verification with one-time passcodes (OTP) &mdash; no
            passwords are stored or managed by our application. Firebase Authentication is SOC 2 and ISO 27001
            certified.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Access Control</h2>
          <p>
            Each business account is fully isolated. Users can only access data belonging to their own
            account and locations. Account owners can configure team members with role-based permissions,
            controlling which sections of the application each user can access. Administrative actions
            such as adding or removing team members are restricted to account owners and admins.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Data Isolation</h2>
          <p>
            All data is logically separated by account and location. Database queries are scoped
            to the authenticated user's account, ensuring that one customer's financial data, maintenance
            records, and operational information is never accessible to another customer.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">GCP Compliance</h2>
          <p>
            Google Cloud Platform maintains a broad set of compliance certifications and attestations,
            including SOC 1/2/3, ISO 27001, ISO 27017, ISO 27018, and PCI DSS. For more details, see{' '}
            <a
              href="https://cloud.google.com/security/compliance"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              Google Cloud Compliance
            </a>.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">AI Chat Assistant</h2>
          <p>
            The in-app chat assistant is powered by <strong>Anthropic's Claude</strong>, a leading AI model
            built with safety and privacy as core design principles. When you use the chat feature, relevant
            context from your account (such as revenue summaries, maintenance records, and inventory data) is
            sent to Anthropic's API to generate helpful responses.
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <strong>No training on your data:</strong> Anthropic does not use API inputs or outputs to
              train its models. Your financial and operational data is never used to improve their AI.
            </li>
            <li>
              <strong>No data retention:</strong> Anthropic's API does not retain prompt or response data
              beyond what is needed to process your request (up to 30 days for trust & safety purposes, with
              no human review unless flagged for safety).
            </li>
            <li>
              <strong>Encrypted in transit:</strong> All communication between our servers and Anthropic's API
              is encrypted using TLS. Your data is never sent directly from your browser to Anthropic &mdash;
              it is routed through our backend, which enforces authentication and account-scoping first.
            </li>
            <li>
              <strong>Account-scoped:</strong> The chat assistant can only access data belonging to your
              account and location. It operates under the same access controls as the rest of the application.
            </li>
          </ul>
          <p className="mt-2">
            For more details on Anthropic's data handling practices, see{' '}
            <a
              href="https://www.anthropic.com/policies/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              Anthropic's Privacy Policy
            </a>.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Payments & Billing</h2>
          <p>
            Subscription payments are processed by <strong>Stripe</strong>, a PCI Level 1 certified payment
            processor trusted by millions of businesses worldwide. Car Wash Copilot never stores, processes,
            or has access to your full credit card number or payment credentials.
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <strong>PCI DSS Level 1:</strong> Stripe maintains the highest level of PCI compliance,
              ensuring your payment data is handled according to the strictest industry standards.
            </li>
            <li>
              <strong>No card data on our servers:</strong> Payment information is entered directly on
              Stripe's hosted checkout page and is never transmitted to or stored on our infrastructure.
            </li>
            <li>
              <strong>Encrypted everywhere:</strong> All payment data is encrypted in transit using TLS
              and encrypted at rest within Stripe's systems.
            </li>
          </ul>
          <p className="mt-2">
            For more details, see{' '}
            <a
              href="https://stripe.com/docs/security"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              Stripe's Security Documentation
            </a>.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">SMS Messaging</h2>
          <p>
            SMS messages (such as team member invitations and work order notifications) are delivered
            through <strong>Twilio</strong>, a trusted enterprise communications platform used by over
            300,000 businesses globally.
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <strong>Limited data shared:</strong> Only the recipient's phone number and the message
              content are sent to Twilio. No financial data, passwords, or account credentials are
              included in SMS messages.
            </li>
            <li>
              <strong>Opt-out support:</strong> Recipients can reply STOP to any message to opt out of
              future SMS notifications at any time. Note: opting out of Twilio messages does not affect
              login verification codes, which are sent separately through Google's Firebase Authentication
              service.
            </li>
            <li>
              <strong>No marketing messages:</strong> We only send transactional messages (invitations
              and operational notifications). We do not send promotional or marketing content via SMS.
            </li>
          </ul>
          <p className="mt-2">
            For more details, see{' '}
            <a
              href="https://www.twilio.com/en-us/security"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              Twilio's Security Page
            </a>{' '}
            and our{' '}
            <Link to="/messaging-consent" className="text-blue-600 hover:text-blue-800">
              Messaging Consent & Terms
            </Link>.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Questions?</h2>
          <p>
            If you have questions about our security practices or data handling, please contact us
            at{' '}
            <a href="mailto:help@carwashcopilot.com" className="text-blue-600 hover:text-blue-800">
              help@carwashcopilot.com
            </a>.
          </p>
        </section>
      </div>

      <div>
        <Link to="/" className="text-sm text-blue-600 hover:text-blue-800">&larr; Back to Dashboard</Link>
      </div>
    </div>
  );
};

export default SecurityPage;
